import { useEffect } from 'react';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import { SEO } from '../../../utils/seo';

export default function RiskAssessmentPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Risk Assessment Services",
    "provider": {
      "@type": "LocalBusiness",
      "name": "LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "North Wales",
        "addressRegion": "Wales",
        "addressCountry": "GB"
      }
    },
    "areaServed": {
      "@type": "Place",
      "name": "North Wales"
    },
    "description": "Professional risk assessment services identifying potential security vulnerabilities and providing actionable recommendations to mitigate threats effectively.",
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Risk Assessment Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Security Audits",
            "description": "Comprehensive security audits examining all aspects of physical and electronic security measures"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Threat Analysis",
            "description": "Detailed threat analysis considering industry, location, and operational context"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Compliance Review",
            "description": "Thorough review of compliance with relevant security and safety regulations"
          }
        }
      ]
    }
  };

  return (
    <>
      <SEO
        title="Risk Assessment North Wales - Security Audits Threat Analysis - LEWIS SECURITY"
        description="Professional risk assessment services identifying potential security vulnerabilities and providing actionable recommendations to mitigate threats effectively. Expert security audits and threat analysis in North Wales."
        keywords="risk assessment North Wales, security audit, threat analysis, vulnerability assessment, compliance review, security risk management"
        schema={schema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-red-900 to-gray-800">
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center text-white">
              <div className="w-20 h-20 bg-red-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-file-shield-2-line text-5xl text-white"></i>
              </div>
              <h1 className="text-5xl sm:text-6xl font-bold mb-6 font-poppins">Risk Assessment Services</h1>
              <p className="text-xl text-gray-200 max-w-3xl mx-auto font-inter">
                Professional risk assessment services identifying potential security vulnerabilities and providing actionable recommendations to mitigate threats effectively
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6 font-poppins">Comprehensive Risk Analysis</h2>
                <p className="text-gray-600 mb-6 leading-relaxed font-inter">
                  Understanding and managing risk is fundamental to protecting your business. At LEWIS SECURITY, our experienced consultants conduct thorough risk assessments that identify vulnerabilities, evaluate potential threats, and provide clear, actionable strategies to enhance your security posture.
                </p>
                <p className="text-gray-600 mb-6 leading-relaxed font-inter">
                  Our systematic approach combines industry best practices with local knowledge of North Wales to deliver risk assessments that are both comprehensive and practical. We help you make informed decisions about security investments and prioritize actions based on actual risk levels.
                </p>
              </div>
              <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/7ad501bd3d4571bd6c2dfc489a258a87.jpg"
                  alt="Risk Assessment Services"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Services Grid */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center font-poppins">Our Risk Assessment Services</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-search-eye-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Security Audits</h3>
                  <p className="text-gray-600 mb-4 font-inter">
                    Comprehensive security audits examining all aspects of your physical and electronic security measures. We evaluate existing systems, identify gaps, and recommend improvements to strengthen your overall security infrastructure.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Physical security assessment</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Electronic systems evaluation</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Access control review</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Vulnerability identification</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-radar-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Threat Analysis</h3>
                  <p className="text-gray-600 mb-4 font-inter">
                    Detailed threat analysis considering your specific industry, location, and operational context. We assess potential threats from various sources and evaluate their likelihood and potential impact on your business.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Industry-specific threat profiling</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Local crime pattern analysis</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Internal and external threats</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Impact assessment</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-file-text-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Compliance Review</h3>
                  <p className="text-gray-600 mb-4 font-inter">
                    Thorough review of your compliance with relevant security and safety regulations. We ensure you meet all legal requirements and industry standards, helping you avoid penalties and maintain certifications.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Regulatory compliance check</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Industry standards verification</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Documentation review</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Gap analysis and remediation</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-file-list-2-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Action Plans</h3>
                  <p className="text-gray-600 mb-4 font-inter">
                    Detailed action plans with prioritized recommendations based on risk levels and business impact. We provide clear, practical steps to improve security, complete with timelines and resource requirements.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Prioritized recommendations</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Implementation timelines</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Cost-benefit analysis</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Progress tracking framework</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Process Section */}
            <div className="bg-gradient-to-br from-red-50 to-gray-50 rounded-2xl p-12 mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center font-poppins">Our Risk Assessment Process</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-white">1</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Initial Consultation</h3>
                  <p className="text-sm text-gray-600 font-inter">Understanding your business, operations, and specific security concerns</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-white">2</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Site Assessment</h3>
                  <p className="text-sm text-gray-600 font-inter">Comprehensive on-site evaluation of premises, systems, and procedures</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-white">3</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Analysis & Report</h3>
                  <p className="text-sm text-gray-600 font-inter">Detailed analysis with findings, risk ratings, and comprehensive documentation</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-white">4</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Action Plan</h3>
                  <p className="text-sm text-gray-600 font-inter">Prioritized recommendations with implementation support and follow-up</p>
                </div>
              </div>
            </div>

            {/* Benefits Section */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center font-poppins">Benefits of Professional Risk Assessment</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                  <i className="ri-shield-check-line text-4xl text-red-600 mb-4"></i>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Enhanced Security</h3>
                  <p className="text-sm text-gray-600 font-inter">Identify and address vulnerabilities before they can be exploited, significantly improving your overall security posture</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                  <i className="ri-money-pound-circle-line text-4xl text-red-600 mb-4"></i>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Cost Optimization</h3>
                  <p className="text-sm text-gray-600 font-inter">Make informed investment decisions by prioritizing security measures based on actual risk levels and business impact</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                  <i className="ri-checkbox-circle-line text-4xl text-red-600 mb-4"></i>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Regulatory Compliance</h3>
                  <p className="text-sm text-gray-600 font-inter">Ensure compliance with all relevant regulations and standards, avoiding penalties and maintaining certifications</p>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="text-center bg-gray-900 rounded-2xl p-12">
              <h2 className="text-3xl font-bold text-white mb-4 font-poppins">Identify Your Security Risks Today</h2>
              <p className="text-gray-300 mb-8 max-w-2xl mx-auto font-inter">
                Don't wait for a security incident. Contact us for a professional risk assessment and take proactive steps to protect your business
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="/#contact" 
                  className="inline-flex items-center justify-center px-8 py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  Request Assessment
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
                <a 
                  href="tel:07834561015" 
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Call 07834 561015
                </a>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
